import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../firebase/firebaseConfig";
import { doc, getDoc, query, where, limit as qlimit, getDocs, updateDoc, deleteDoc, collection, addDoc, onSnapshot, orderBy, } from "firebase/firestore";
import { serverTimestamp } from "firebase/firestore";
import Button from "../../components/Button";
import * as XLSX from "xlsx-js-style";
import { useClaims } from "../../providers/ClaimsProvider";

// ---------- Utils ----------
const normalizeSexo = (v) => {
  if (v == null) return "";
  const s = String(v).trim().toLowerCase();
  if (["f", "femenino", "female", "mujer"].includes(s)) return "Femenino";
  if (["m", "masculino", "male", "hombre"].includes(s)) return "Masculino";
  return "Otro";
};

const computeIMC = (pesoKg, tallaCm) => {
  const w = Number(pesoKg);
  const hCm = Number(tallaCm);
  if (!w || !hCm || w <= 0 || hCm <= 0) return null;
  const hM = hCm / 100;
  const imc = w / (hM * hM);
  return isFinite(imc) ? Number(imc.toFixed(2)) : null;
};

const formatDateDMY = (f) => {
  if (!f) return "—";
  if (typeof f === "string" && /^\d{4}-\d{2}-\d{2}$/.test(f)) {
    const [y, m, d] = f.split("-");
    return `${d}-${m}-${y}`;
  }
  if (f?.seconds) {
    const d = new Date(f.seconds * 1000);
    if (!isNaN(d)) return d.toLocaleDateString("es-ES");
  }
  const d = new Date(f);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("es-ES");
};

const visitaToDate = (v) => {
  if (v?.createdAt?.seconds) return new Date(v.createdAt.seconds * 1000);
  if (typeof v?.fecha === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v.fecha)) {
    return new Date(`${v.fecha}T00:00:00`);
  }
  if (v?.fecha?.seconds) return new Date(v.fecha.seconds * 1000);
  return new Date(0);
};

const safeSheetName = (name) => {
  return String(name || "Hoja")
    .replace(/[*?:\\[\]]/g, " ")
    .slice(0, 31)
    .trim();
};

// ---------- Clasificación cribado nutricional ----------
const clasificarCribadoNutricional = (tipoCribado, puntuacion) => {
  switch (tipoCribado) {
    case "MNA":
      if (puntuacion > 24) return "Estado nutricional normal > 24";
      if (puntuacion >= 17) return "Riesgo de malnutrición 17-24";
      return "Malnutrición < 17";

    case "MST":
      return puntuacion >= 2
        ? "Riesgo de malnutrición"
        : "Sin riesgo";

    case "NRS":
      return puntuacion >= 3
        ? "Riesgo de malnutrición ≥ 3"
        : "Sin riesgo";

    case "MUST":
      if (puntuacion === 0) return "Riesgo bajo = 0";
      if (puntuacion === 1) return "Riesgo medio = 1";
      return "Riesgo alto = 2 o +";

    default:
      return "";
  }
};

export default function PacienteDetalle() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { user, role } = useClaims();

  const [cargando, setCargando] = useState(true);
  const [docId, setDocId] = useState(null);
  const [data, setData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [error, setError] = useState("");

  // Visitas
  const [visitas, setVisitas] = useState([]);
  const visitasRef = useMemo(() => {
    if (!docId) return null;
    return collection(db, "pacientes", docId, "visitas");
  }, [docId]);

  const [eliminandoVisitaId, setEliminandoVisitaId] = useState(null);

  // ⭐ NUEVO: Estado del buscador
  const [searchVisita, setSearchVisita] = useState("");

  // ---- Cargar Paciente ----
  useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      setError("");
      try {
        const refById = doc(db, "pacientes", paramId);
        const byIdSnap = await getDoc(refById);

        let raw, theId;
        if (byIdSnap.exists()) {
          raw = byIdSnap.data();
          theId = byIdSnap.id;
        } else {
          const qq = query(
            collection(db, "pacientes"),
            where("codigo", "==", paramId),
            qlimit(1)
          );
          const res = await getDocs(qq);
          if (res.empty) {
            setError("No se encontró el paciente.");
            setData(null);
            setDocId(null);
            return;
          }
          const qSnap = res.docs[0];
          raw = qSnap.data();
          theId = qSnap.id;
        }

        setDocId(theId);

        let fechaISO = "";
        const f = raw.fecha;
        if (!f) fechaISO = "";
        else if (typeof f === "string") fechaISO = f.slice(0, 10);
        else if (f?.seconds) {
          const d = new Date(f.seconds * 1000);
          fechaISO = d.toISOString().slice(0, 10);
        }

        const normal = {
          codigo: raw.codigo || "",
          fecha: fechaISO,
          sexo: normalizeSexo(raw.sexo),
          edad: raw.edad ?? "",
          peso: raw.peso ?? "",
          talla: raw.talla ?? "",
          creadoPor: raw.creadoPor || "",
          creadoPorEmail: raw.creadoPorEmail || "",
        };

        setData(normal);
        setOriginalData(normal);
      } catch (e) {
        console.error(e);
        setError("Error cargando el paciente.");
      } finally {
        setCargando(false);
      }
    };
    if (paramId) cargar();
  }, [paramId]);

  // ---- Suscripción a visitas ----
  useEffect(() => {
    if (!visitasRef) return;
const unsub = onSnapshot(
  query(visitasRef, orderBy("createdAt", "asc")),
  (snap) => {
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setVisitas(rows);
  }
);
    return () => unsub();
  }, [visitasRef]);

  const visitasAsc = useMemo(() => {
    return [...visitas].sort((a, b) => visitaToDate(a) - visitaToDate(b));
  }, [visitas]);

  const numeroVisitaPorId = useMemo(() => {
    const map = {};
    visitasAsc.forEach((v, i) => (map[v.id] = i + 1));
    return map;
  }, [visitasAsc]);

  // ⭐ NUEVO: filtro dinámico
  const visitasFiltradas = useMemo(() => {
    if (!searchVisita.trim()) return visitasAsc;

    const term = searchVisita.toLowerCase();

    return visitasAsc.filter((v) => {
      const numero = numeroVisitaPorId[v.id]?.toString() || "";
      const fecha = formatDateDMY(v.fecha)?.toLowerCase() || "";
      const estado = (v?.estado || (v?.completada ? "Completo" : "Pendiente"))
        .toLowerCase();

      return (
        numero.includes(term) ||
        fecha.includes(term) ||
        estado.includes(term)
      );
    });
  }, [searchVisita, visitasAsc, numeroVisitaPorId]);

  const puedeEditar = useMemo(() => {
    if (!user || !data) return false;
    if (role === "investigador_principal") return true;
    return data.creadoPor === user.uid;
  }, [user, role, data]);

  const onChange = (field, value) => {
    if (!editando) return;
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const imc = useMemo(
    () => computeIMC(data?.peso, data?.talla),
    [data?.peso, data?.talla]
  );

  const guardar = async () => {
    if (!docId || !data || !puedeEditar || !editando) return;
    setGuardando(true);
    setError("");
    try {
      if (!data.fecha) throw new Error("La fecha es obligatoria.");
      if (!data.sexo) throw new Error("El sexo es obligatorio.");

      const payload = {
        codigo: data.codigo || "",
        fecha: data.fecha,
        sexo: normalizeSexo(data.sexo),
        edad: data.edad === "" ? null : Number(data.edad),
        peso: data.peso === "" ? null : Number(data.peso),
        talla: data.talla === "" ? null : Number(data.talla),
        imc: imc ?? null,
      };

      await updateDoc(doc(db, "pacientes", docId), payload);

      setOriginalData({
        ...payload,
        creadoPor: data.creadoPor,
        creadoPorEmail: data.creadoPorEmail,
      });
      setData((prev) => ({ ...prev, ...payload }));
      setEditando(false);
      alert("Cambios guardados ✅");
    } catch (e) {
      console.error("update paciente error:", e.code, e.message);
      setError(e.message || "No se pudo guardar.");
    } finally {
      setGuardando(false);
    }
  };

const exportarPaciente = async () => {
  if (!data || !docId) return;

  const wb = XLSX.utils.book_new();

  // ======================
  // HOJA 1: PACIENTE
  // ======================
const wsPaciente = XLSX.utils.json_to_sheet([
  {
    "Código": data.codigo || "",
    "Fecha de registro": formatDateDMY(data.fecha),
    "Sexo": data.sexo || "",
    "Edad": data.edad ?? "",
    "Peso inicial (kg)": data.peso ?? "",
    "Talla inicial (cm)": data.talla ?? "",
    "IMC inicial": imc ?? "",
    "Creado por…": data.creadoPorEmail || data.creadoPor || "",
  },
]);
XLSX.utils.book_append_sheet(wb, wsPaciente, "Paciente");

// Ancho de columnas – Paciente
wsPaciente["!cols"] = [
  { wch: 25 }, // Código
  { wch: 18 }, // Fecha de registro
  { wch: 12 }, // Sexo
  { wch: 10 }, // Edad
  { wch: 16 }, // Peso inicial (kg)
  { wch: 16 }, // Talla inicial (cm)
  { wch: 14 }, // IMC inicial
  { wch: 45 }, // Creado por…
];

// Centrado horizontal y vertical – Paciente
{
  const range = XLSX.utils.decode_range(wsPaciente["!ref"]);
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = wsPaciente[addr];
      if (!cell) continue;

      cell.s = {
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
        font: {
          sz: 12,
          bold: R === 0, // 👈 solo la primera fila
        },
      };
    }
  }
}

  // ======================
  // HOJA 2: VISITAS (RESUMEN)
  // ======================
const wsVisitas = XLSX.utils.json_to_sheet(
  visitasAsc.map((v) => ({
    "Número de visita": numeroVisitaPorId[v.id],
    "Fecha de la visita": formatDateDMY(v.fecha),
    "Estado de la visita":
      v.estado || (v.completada ? "Completo" : "Pendiente"),
    "Peso (kg)": v.medidas?.peso ?? v.peso ?? "",
    "Talla (cm)": v.medidas?.talla ?? v.talla ?? "",
    "IMC": v.medidas?.imc ?? v.imc ?? "",
  }))
);
  XLSX.utils.book_append_sheet(wb, wsVisitas, "Visitas (Resumen)");

// Ancho de columnas – Visitas (Resumen)
wsVisitas["!cols"] = [
  { wch: 20 }, // Número de visita
  { wch: 18 }, // Fecha de la visita
  { wch: 18 }, // Estado de la visita
  { wch: 16 }, // Peso (kg)
  { wch: 16 }, // Talla (cm)
  { wch: 14 }, // IMC
];

// Centrado horizontal y vertical – Visitas (Resumen)
{
  const range = XLSX.utils.decode_range(wsVisitas["!ref"]);
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = wsVisitas[addr];
      if (!cell) continue;

      cell.s = {
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
        font: {
          sz: 12,
          bold: R === 0, // 👈 solo la primera fila
        },
      };
    }
  }
}

  // ======================
  // HOJAS POR VISITA
  // ======================
  for (const v of visitasAsc) {
    const numero = numeroVisitaPorId[v.id];

// ======================
// CUESTIONARIOS (1 HOJA POR CUESTIONARIO)
// ======================
const respSnap = await getDocs(
  collection(db, "pacientes", docId, "visitas", v.id, "respuestas")
);

const ORDEN_CUESTIONARIOS = [
  "EAT-10",
  "CRIBADO-NUTRICIONAL",
  "CRITERIOS-GLIM",
  "CAVIDAD-ORAL",
  "MECV-V",
];

const LABEL_CUESTIONARIOS = {
  "EAT-10": "Cribado disfagia",
  "CRIBADO-NUTRICIONAL": "Cribado nutricional",
  "CRITERIOS-GLIM": "Val. nutricional",
  "CAVIDAD-ORAL": "Val. cavidad oral",
  "MECV-V": "MECV-V",
};

// ---------------------
// EAT-10
// ---------------------
const EAT10_PREGUNTAS = {
  "0": "1. Mi problema para tragar me ha llevado a perder peso",
  "1": "2. Mi problema para tragar interfiere con mi capacidad para comer fuera de casa",
  "2": "3. Tragar líquidos me supone un esfuerzo extra",
  "3": "4. Tragar sólidos me supone un esfuerzo extra",
  "4": "5. Tragar pastillas me supone un esfuerzo extra",
  "5": "6. Tragar es doloroso",
  "6": "7. El placer de comer se ve afectado por mi problema para tragar",
  "7": "8. Cuando trago, la comida se pega en mi garganta",
  "8": "9. Toso cuando como",
  "9": "10. Tragar es estresante",
};

const EAT10_RESPUESTAS = {
  "0": "0 - Ningún problema",
  "4": "4 - Problema serio",
};

// ---------------------
// CRIBADO NUTRICIONAL
// ---------------------

const TIPO_PACIENTE_LABEL = {
  "Paciente mayor de 65 años": "Paciente mayor de 65 años (MNA)",
  "Paciente con cáncer": "Paciente con cáncer (MST)",
  "Paciente hospitalizado": "Paciente hospitalizado (NRS)",
  "Paciente ambulatorio": "Paciente ambulatorio (MUST)",
};

const MNA_PREGUNTAS = {
  A: "A. ¿Ha perdido el apetito?... últimos 3 meses?",
  B: "B. Pérdida reciente de peso (< 3 meses)",
  C: "C. Movilidad",
  D: "D. ¿Enfermedad aguda o estrés psicológico en los últimos 3 meses?",
  E: "E. Problemas neuropsicológicos",
  F: "F. Índice de masa corporal (IMC)",
  G: "G. ¿El paciente vive independiente en su domicilio?",
  H: "H. ¿Toma más de 3 medicamentos al día?",
  I: "I. ¿Úlceras o lesiones cutáneas?",
  J: "J. ¿Cuántas comidas completas toma al día?",
  K: "K. ¿Consume el paciente…",
  K1: "…productos lácteos al menos una vez al día?",
  K2: "…huevos o legumbres 1 o 2 veces a la semana?",
  K3: "…carne, pescado o aves, diariamente?",
  L: "L. ¿Consume frutas o verduras al menos 2 veces al día?",
  M: "M. ¿Cuántos vasos de agua u otros líquidos toma al día?",
  N: "N. Forma de alimentarse",
  O: "O. ¿Se considera el paciente que está bien nutrido?",
  P: "P. En comparación con las personas de su edad, ¿cómo encuentra su estado de salud?",
  Q: "Q. Circunferencia braquial (CB en cm)",
  R: "R. Circunferencia de la pantorrilla (CP en cm)",
};

const MNA_RESPUESTAS = {
  A: {
    "0": "0 - ha comido mucho menos",
    "1": "1 - ha comido menos",
    "2": "2 - ha comido igual",
  },
  B: {
    "0": "0 - > 3 kg",
    "1": "1 - no lo sabe",
    "2": "2 - pérdida entre 1 a 3 kg",
    "3": "3 - no ha habido peso",
  },
  C: {
    "0": "0 - de la cama al sillón",
    "1": "1 - autonomía en el interior",
    "2": "2 - sale del domicilio",
  },
  D: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  E: {
    "0": "0 - demencia o depresión grave",
    "1": "1 - demencia leve",
    "2": "2 - sin problemas psicológicos",
  },
  F: {
    "0": "0 - IMC < 19",
    "1": "1 - 19 ≤ IMC < 21",
    "2": "2 - 21 ≤ IMC < 23",
    "3": "3 - IMC ≥ 23",
  },
  G: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  H: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  I: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  J: {
    "0": "0 - 1 comida",
    "1": "1 - 2 comidas",
    "2": "2 - 3 comidas",
  },
  K1: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  K2: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  K3: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  L: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  M: {
    "0": "0 - menos de 3 vasos",
    "0.5": "0.5 - de 3 a 5 vasos",
    "1": "1 - más de 5 vasos",
  },
  N: {
    "0": "0 - necesita ayuda",
    "1": "1 - se alimenta solo con dificultad",
    "2": "2 - se alimenta solo sin dificultad",
  },
  O: {
    "0": "0 - malnutrición grave",
    "1": "1 - no lo sabe o malnutrición moderada",
    "2": "2 - sin problemas de nutrición",
  },
  P: {
    "0": "0 - peor",
    "0.5": "0.5 - no lo sabe",
    "1": "1 - igual",
    "2": "2 - mejor",
  },
  Q: {
    "0": "0 - CB < 21",
    "0.5": "0.5 - 21 ≤ CB ≤ 22",
    "1": "1 - CB > 22",
  },
  R: {
    "0": "0 - CP < 31",
    "1": "1 - CP ≥ 31",
  },
};

const MST_PREGUNTAS = {
  q1: "1. ¿Ha perdido peso de manera involuntaria recientemente?",
  q1b: "Si ha perdido peso, ¿cuántos kilogramos?",
  q2: "2. ¿Ha comido menos de lo normal a causa de la disminución del apetito?",
};

const MST_RESPUESTAS = {
  q1: {
    "0": "0 - no",
    "2": "2 - no estoy seguro",
  },
  q1b: {
    "1": "1 - de 1 a 5 kg",
    "2": "2 - de 6 a 10 kg",
    "3": "3 - de 11 a 15 kg",
    "4": "4 - > 15 kg",
    "0": "0 - no estoy seguro",
  },
  q2: {
    "0": "0 - no",
    "1": "1 - sí",
  },
};

const NRS_PREGUNTAS = {
  n1: "1. ¿IMC < 20.5?",
  n2: "2. ¿Pérdida de peso en los últimos 3 meses?",
  n3: "3. ¿Disminución de la ingesta en la última semana?",
  n4: "4. ¿Enfermedad grave?",
};

const NRS_RESPUESTAS = {
  n1: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  n2: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  n3: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  n4: {
    "0": "0 - no",
    "1": "1 - sí",
  },
};

const MUST_PREGUNTAS = {
  step1: "Paso 1. IMC = peso en kg / talla en m²",
  step2: "Paso 2. Pérdida de peso involuntaria en los últimos 3-6 meses",
  step3: "Paso 3. Paciente con enfermedad aguda y sin aporte > 5 días",
};

const MUST_RESPUESTAS = {
  step1: {
    "0": "0 - ≥ 20",
    "1": "1 - 18.5 a 20",
    "2": "2 - ≤ 18.5",
  },
  step2: {
    "0": "0 - ≤ 5 %",
    "1": "1 - 5 a 10 %",
    "2": "2 - ≥ 10 %",
  },
  step3: {
    "0": "0 - no",
    "1": "1 - sí",
  },
};

// ---------------------
// GLIM – Preguntas
// ---------------------
const GLIM_PREGUNTAS = {
  p1: "CRITERIOS FENOTÍPICOS - 1. Pérdida de peso",
  p2: "CRITERIOS FENOTÍPICOS - 2. Índice de Masa Corporal (IMC)",
  p3: "CRITERIOS FENOTÍPICOS - 3. Masa muscular reducida",
  p4: "CRITERIOS ETIOLÓGICOS - 4. Ingesta o asimilación reducida",
  p5: "CRITERIOS ETIOLÓGICOS - 5. Enfermedad o inflamación",
};

// ---------------------
// CAVIDAD-ORAL – Preguntas
// ---------------------
const CAVIDAD_ORAL_PREGUNTAS = {
  q1: "1. Piezas dentarias",
  q2: "2. Dentadura postiza",
  q3: "3. Mucositis",
  q4: "4. Lengua y encías",
  q5: "5. Salivación",
  q6: "6. Halitosis (mal aliento)",
  q7: "7. Dolor o molestias orales",
};

// Agrupar por instrumento
const respuestasPorInstrumento = {};
respSnap.forEach((d) => {
  const q = d.data();
  const id = q.instrumentoId || d.id;
  respuestasPorInstrumento[id] = q;
});

// Crear hojas por cuestionario
for (const instrumentoId of ORDEN_CUESTIONARIOS) {
  const q = respuestasPorInstrumento[instrumentoId];
  if (!q) continue;

  const nombreHoja = safeSheetName(
    `Visita ${numero} – ${LABEL_CUESTIONARIOS[instrumentoId]}`
  );

  const estadoTexto = q.completado ? "Completo" : "Pendiente";
  const rows = [];

  // =========================
  // EAT-10 (FORMATO ESPECIAL)
  // =========================
  if (instrumentoId === "EAT-10") {

    const puntuacionTotal = Object.values(q.items || {}).reduce(
      (acc, v) => acc + Number(v || 0),
      0
    );

    rows.push({
      "Nombre del cuestionario": "Cribado disfagia (EAT-10)",
      "Estado": estadoTexto,
      "Preguntas": "",
      "Respuestas": "",
      "Puntuación total": "",
      "Resultado": "",
    });

    Object.entries(q.items || {}).forEach(([k, v]) => {
      rows.push({
        "Nombre del cuestionario": "",
        "Estado": "",
        "Preguntas": EAT10_PREGUNTAS[k] || k,
        "Respuestas": EAT10_RESPUESTAS[String(v)] ?? String(v ?? ""),
        "Puntuación total": "",
        "Resultado": "",
      });
    });

    rows.push({
      "Nombre del cuestionario": "",
      "Estado": "",
      "Preguntas": "",
      "Respuestas": "",
      "Puntuación total": puntuacionTotal,
      "Resultado":
        puntuacionTotal >= 3
          ? "Presenta riesgo de disfagia"
          : "No presenta riesgo de disfagia",
    });
  }

  // =================================
  // CRIBADO NUTRICIONAL (FORMATO ESPECIAL)
  // =================================
  else if (instrumentoId === "CRIBADO-NUTRICIONAL") {

const tipoPacienteRaw = q.items?.__selector ?? "";
const tipoPacienteTexto = TIPO_PACIENTE_LABEL[tipoPacienteRaw] ?? tipoPacienteRaw;

    const tipoCribado = q.items?.__tipoPaciente;

    const respuestasCribado =
      tipoCribado === "MNA"  ? q.items?.mna  :
      tipoCribado === "MST"  ? q.items?.mst  :
      tipoCribado === "NRS"  ? q.items?.nrs  :
      tipoCribado === "MUST" ? q.items?.must :
      {};

let puntuacionTotal = 0;

Object.entries(respuestasCribado || {}).forEach(([key, value]) => {
  // En MNA, NO sumar subpreguntas K1, K2, K3
  if (tipoCribado === "MNA" && ["K1", "K2", "K3"].includes(key)) {
    return;
  }

  const n = Number(value);
  if (!isNaN(n)) {
    puntuacionTotal += n;
  }
});

rows.push({
  "Nombre del cuestionario": LABEL_CUESTIONARIOS[instrumentoId],
  "Estado": estadoTexto,
  "Tipo de paciente": tipoPacienteTexto,
  "Preguntas": "",
  "Respuestas": "",
  "Puntuación total": "",
  "Clasificación": "",
});

const entriesOrdenadas =
  tipoCribado === "MNA"
    ? Object.entries(respuestasCribado || {}).sort(([a], [b]) =>
        a.localeCompare(b, "es", { numeric: true })
      )
    : tipoCribado === "MST"
      ? ["q1", "q1b", "q2"]
          .filter((k) => k in (respuestasCribado || {}))
          .map((k) => [k, respuestasCribado[k]])
    : tipoCribado === "NRS"
      ? ["n1", "n2", "n3", "n4"]
          .filter((k) => k in (respuestasCribado || {}))
          .map((k) => [k, respuestasCribado[k]])
    : tipoCribado === "MUST"
      ? ["step1", "step2", "step3"]
          .filter((k) => k in (respuestasCribado || {}))
          .map((k) => [k, respuestasCribado[k]])
      : Object.entries(respuestasCribado || {});

entriesOrdenadas.forEach(([key, value]) => {

  // En MNA, NO mostrar la pregunta K agregada
  if (tipoCribado === "MNA" && key === "K") return;

  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Tipo de paciente": "",
"Preguntas":
  tipoCribado === "MNA"
    ? (MNA_PREGUNTAS[key] || key)
    : tipoCribado === "MST"
      ? (MST_PREGUNTAS[key] || key)
      : tipoCribado === "NRS"
        ? (NRS_PREGUNTAS[key] || key)
        : tipoCribado === "MUST"
          ? (MUST_PREGUNTAS[key] || key)
          : key,
"Respuestas":
  tipoCribado === "MNA"
    ? (MNA_RESPUESTAS[key]?.[String(value)] ?? String(value ?? ""))
    : tipoCribado === "MST"
      ? (MST_RESPUESTAS[key]?.[String(value)] ?? String(value ?? ""))
      : tipoCribado === "NRS"
        ? (NRS_RESPUESTAS[key]?.[String(value)] ?? String(value ?? ""))
        : tipoCribado === "MUST"
          ? (MUST_RESPUESTAS[key]?.[String(value)] ?? String(value ?? ""))
          : String(value ?? ""),
    "Puntuación total": "",
    "Clasificación": "",
  });
});

rows.push({
  "Nombre del cuestionario": "",
  "Estado": "",
  "Tipo de paciente": "",
  "Preguntas": "",
  "Respuestas": "",
  "Puntuación total": puntuacionTotal,
  "Clasificación": clasificarCribadoNutricional(
    tipoCribado,
    puntuacionTotal
  ),
});
  }

// =================================
// VALORACIÓN NUTRICIONAL (GLIM)
// =================================

else if (instrumentoId === "CRITERIOS-GLIM") {

  const GLIM_ORDEN = ["p1", "p2", "p3", "p4", "p5"];

  const resultadoGLIM = q.resultado;

  // Cabecera
  rows.push({
    "Nombre del cuestionario": LABEL_CUESTIONARIOS[instrumentoId],
    "Estado": estadoTexto,
    "Preguntas": "",
    "Respuestas": "",
    "Resultado": "",
  });

  // Preguntas fenotípicas y etiológicas
GLIM_ORDEN.forEach((key) => {
  const value = q.items?.glim?.[key];
  if (value == null) return;

  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Preguntas": GLIM_PREGUNTAS[key] || key,
    "Respuestas": String(value),
    "Resultado": "",
  });
});

  // Fila final con resultado
  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Preguntas": "",
    "Respuestas": "",
    "Resultado": resultadoGLIM,
  });
}

// =========================
// CAVIDAD ORAL (FORMATO ESPECIAL)
// =========================
else if (instrumentoId === "CAVIDAD-ORAL") {

const respuestas = q.items?.cavidadoral || [];

rows.push({
  "Nombre del cuestionario": LABEL_CUESTIONARIOS[instrumentoId],
  "Estado": estadoTexto,
  "Preguntas": "",
  "Respuestas": "",
});

["q1", "q2", "q3", "q4", "q5", "q6", "q7"].forEach((key) => {
  if (!(key in respuestas)) return;

  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Preguntas": CAVIDAD_ORAL_PREGUNTAS[key],
    "Respuestas": String(respuestas[key] ?? ""),
  });
});
}

// =========================
// CAVIDAD ORAL (FORMATO ESPECIAL)
// =========================
else if (instrumentoId === "CAVIDAD-ORAL") {

  const respuestas = q.items?.cavidadoral || {};

  rows.push({
    "Nombre del cuestionario": LABEL_CUESTIONARIOS[instrumentoId],
    "Estado": estadoTexto,
    "Preguntas": "",
    "Respuestas": "",
  });

["q1", "q2", "q3", "q4", "q5", "q6", "q7"]
  .filter((k) => k in respuestas)
  .forEach((key) => {
    rows.push({
      "Nombre del cuestionario": "",
      "Estado": "",
      "Preguntas": CAVIDAD_ORAL_PREGUNTAS[key] || key,
      "Respuestas": String(respuestas[key] ?? ""),
    });
  });
}

// =========================
// MECV-V (FORMATO ESPECIAL)
// =========================
else if (instrumentoId === "MECV-V") {

const mecvv = q.items?.mecvv || {};
const resultado = q.resultado || "";

// 1️⃣ Extraer signos marcados
const seguridad = [];
const eficacia = [];

Object.keys(mecvv).forEach((k) => {
  if (!mecvv[k]) return;

  const [, tipo, signo, viscosidad, volumen] =
    k.match(/(seg|efi)_(.+?)_(nectar|liquido|pudin)_(\d+)/) || [];

  if (!tipo) return;

  const texto = `${signo} ${viscosidad} ${volumen}`;

  if (tipo === "seg") seguridad.push(texto);
  if (tipo === "efi") eficacia.push(texto);
});

// 2️⃣ Recomendaciones dietéticas en ARRAY
const recomendacionesArr = [];

if (mecvv.viscosidad) {
  recomendacionesArr.push(`Viscosidad: ${mecvv.viscosidad}`);
}
if (mecvv.volumen) {
  recomendacionesArr.push(`Volumen: ${mecvv.volumen}`);
}

// 3️⃣ Fila cabecera
rows.push({
  "Nombre del cuestionario": "MECV-V",
  "Estado": estadoTexto,
  "Alteraciones o signos de seguridad": "",
  "Alteraciones o signos de eficacia": "",
  "Resultado": "",
  "Recomendaciones dietéticas": "",
});

// 4️⃣ Seguridad → una fila por signo
seguridad.forEach((s) => {
  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Alteraciones o signos de seguridad": s,
    "Alteraciones o signos de eficacia": "",
    "Resultado": "",
    "Recomendaciones dietéticas": "",
  });
});

// 5️⃣ Eficacia → una fila por signo
eficacia.forEach((e) => {
  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Alteraciones o signos de seguridad": "",
    "Alteraciones o signos de eficacia": e,
    "Resultado": "",
    "Recomendaciones dietéticas": "",
  });
});

// 6️⃣ Resultado → su propia fila
rows.push({
  "Nombre del cuestionario": "",
  "Estado": "",
  "Alteraciones o signos de seguridad": "",
  "Alteraciones o signos de eficacia": "",
  "Resultado": resultado,
  "Recomendaciones dietéticas": "",
});

// 7️⃣ Recomendaciones → una fila por elemento
recomendacionesArr.forEach((r) => {
  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Alteraciones o signos de seguridad": "",
    "Alteraciones o signos de eficacia": "",
    "Resultado": "",
    "Recomendaciones dietéticas": r,
  });
});
}

  // =========================
  // RESTO (FORMATO ORIGINAL)
  // =========================
  else {
    rows.push({
      "Nombre del cuestionario": LABEL_CUESTIONARIOS[instrumentoId],
      "Estado": estadoTexto,
      "Campo": "",
      "Valor": "",
    });

    Object.entries(q.items || {}).forEach(([campo, valor]) => {
      rows.push({
        "Nombre del cuestionario": "",
        "Estado": "",
        "Campo": campo,
        "Valor":
          typeof valor === "object"
            ? JSON.stringify(valor)
            : String(valor ?? ""),
      });
    });
  }

  const ws = XLSX.utils.json_to_sheet(rows);

  // Ancho de columnas
  if (instrumentoId === "EAT-10") {
    ws["!cols"] = [
      { wch: 35 },
      { wch: 14 },
      { wch: 80 },
      { wch: 25 },
      { wch: 25 },
      { wch: 30 },
    ];
  } else if (instrumentoId === "CRIBADO-NUTRICIONAL") {
    ws["!cols"] = [
      { wch: 30 },
      { wch: 14 },
      { wch: 30 },
      { wch: 90 },
      { wch: 38 },
      { wch: 25 },
      { wch: 35 },
    ];
} else if (instrumentoId === "CRITERIOS-GLIM") {
  ws["!cols"] = [
    { wch: 30 },
    { wch: 14 },
    { wch: 70 },
    { wch: 70 },
    { wch: 30 },
  ]; 
} else if (instrumentoId === "CAVIDAD-ORAL") {
  ws["!cols"] = [
    { wch: 30 },
    { wch: 14 },
    { wch: 40 },
    { wch: 40 },
  ];
} else if (instrumentoId === "MECV-V") {
  ws["!cols"] = [
    { wch: 30 }, 
    { wch: 14 }, 
    { wch: 45 }, 
    { wch: 45 }, 
    { wch: 60 }, 
    { wch: 40 }, 
  ];
} else {
    ws["!cols"] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 40 },
      { wch: 40 },
    ];
  }

  XLSX.utils.book_append_sheet(wb, ws, nombreHoja);

  // Estilos
  if (ws["!ref"]) {
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[addr];
        if (!cell) continue;

        cell.s = {
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          },
          font: {
            sz: 12,
            bold: R === 0,
          },
        };
      }
    }
  }
}

// ---- PAUTAS ----
    const pautasRef = doc(
      db,
      "pacientes",
      docId,
      "visitas",
      v.id,
      "pautas",
      "main"
    );
    const pautasSnap = await getDoc(pautasRef);

if (pautasSnap.exists()) {
  const pautas = pautasSnap.data();

const ORDEN_PAUTAS = [
  "valdisfagiacondiag",
  "valdisfagiaconsos",
  "recomendaciones",
  "recetas",
  "ejercicios",
  "pautasejercicioslogrehab",
  "farmacos",
];

const LABEL_PAUTAS = {
  valdisfagiacondiag:
    "Valoración disfagia: Usuario/a con diagnóstico de disfagia orofaríngea",
  valdisfagiaconsos:
    "Valoración disfagia: Usuario/a con sospecha de disfagia orofaríngea",
  recomendaciones: "Recomendaciones",
  recetas: "Recetas",
  ejercicios: "Actividad / Ejercicio físico",
  pautasejercicioslogrehab:
    "Pautas / Ejercicios de logopedia y rehabilitación",
  farmacos: "Disfagia y fármacos",
};

const aoaPautas = [
  [
    "Nombre de la sección a la que pertenece la recomendación o pauta",
    "Recomendaciones y pautas seleccionadas para el paciente",
  ],
  ...ORDEN_PAUTAS.flatMap((k) => {
    if (pautas[k] == null) return [];

    const label = LABEL_PAUTAS[k] || k;
    const value = pautas[k];

    if (Array.isArray(value)) {
      return value.map((item) => [
        label,
        typeof item === "object"
          ? JSON.stringify(item)
          : String(item ?? ""),
      ]);
    }

    if (typeof value === "object") {
      return Object.entries(value).map(([_, v]) => [
        label,
        typeof v === "object"
          ? JSON.stringify(v)
          : String(v ?? ""),
      ]);
    }

    return [[label, String(value)]];
  }),
];

  const wsP = XLSX.utils.aoa_to_sheet(aoaPautas);
  XLSX.utils.book_append_sheet(
    wb,
    wsP,
    `Visita ${numero} - Pautas`
  );

// Ancho de columnas – Pautas
wsP["!cols"] = [
  { wch: 80 },
  { wch: 100 },
];

  // Estilo: centrado, tamaño 12 y encabezados en negrita
  const range = XLSX.utils.decode_range(wsP["!ref"]);
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = wsP[addr];
      if (!cell) continue;

      cell.s = {
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
        font: {
          sz: 12,
          bold: R === 0,
        },
      };
    }
  }
}

  }

  // ======================
  // DESCARGA
  // ======================
XLSX.writeFile(
  wb,
  `paciente_${paramId}_completo.xlsx`,
  {
    bookType: "xlsx",
    cellStyles: true,
  }
);
};

  const eliminarPaciente = async () => {
    if (!puedeEditar || !docId) return;
    const ok = window.confirm(
      "¿Eliminar el paciente? Esta acción no se puede deshacer."
    );
    if (!ok) return;
    setEliminando(true);
    setError("");
    try {
      await deleteDoc(doc(db, "pacientes", docId));
      alert("Paciente eliminado 🗑️");
      navigate(-1);
    } catch (e) {
      console.error(e);
      setError("No se pudo eliminar.");
    } finally {
      setEliminando(false);
    }
  };

  const crearVisita = async () => {
    if (!visitasRef || !user || !data) return;
    const hoyISO = new Date().toISOString().slice(0, 10);

    const pesoBase = data.peso === "" ? null : Number(data.peso);
    const tallaBase = data.talla === "" ? null : Number(data.talla);
    const imcBase = computeIMC(pesoBase, tallaBase);
    const edadNum = data.edad === "" ? null : Number(data.edad);

    const numeroVisita = visitas.length + 1;

    const payload = {
      fecha: hoyISO,
      numeroVisita,
      completada: false,
      notas: "",
      peso: pesoBase,
      talla: tallaBase,
      imc: imcBase,
      pesoBase: pesoBase,
      medidas: { peso: pesoBase, talla: tallaBase, imc: imcBase, edad: edadNum },
      creadoPor: user.uid,
      creadoPorEmail: user.email || "",
      createdAt: serverTimestamp(), 
    };

    try {
      const newRef = await addDoc(visitasRef, payload);
      navigate(`/pacientes/${docId}/visitas/${newRef.id}`);
    } catch (e) {
      console.error("create visita error:", e.code, e.message);
      alert(`No se pudo crear la visita (${e.code}).`);
    }
  };

  const eliminarVisita = async (visitaId) => {
    if (!puedeEditar || !docId || !visitaId) return;
    const ok = window.confirm(
      "¿Eliminar esta visita? Esta acción no se puede deshacer."
    );
    if (!ok) return;
    setEliminandoVisitaId(visitaId);
    try {
      await deleteDoc(doc(db, "pacientes", docId, "visitas", visitaId));
    } catch (e) {
      console.error("delete visita error:", e.code, e.message);
      alert("No se pudo eliminar la visita.");
    } finally {
      setEliminandoVisitaId(null);
    }
  };


  const fieldDisabled = !puedeEditar || !editando || guardando;

  return (
    <div className="p-4 max-w-xl mx-auto flex flex-col gap-4">

      {/* Barra superior */}
      <div className="flex justify-start">
        <Button
          onClick={() => navigate("/mi-base-de-datos")}
          className="flex items-center gap-1 px-3 py-1.5"
        >
          <span>Volver a mi base de datos</span>
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <h4 className="font-bold">Paciente</h4>
        <Button
          type="button"
          onClick={exportarPaciente}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          Exportar Excel
        </Button>
      </div>

      {/* Si carga o error */}
      {cargando ? (
        <p>Cargando…</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : !data ? (
        <p>No hay datos para mostrar.</p>
      ) : (
        <>
          {/* FORMULARIO BASE */}
          <form
            className="bg-[#dfeaf7] p-6 rounded-lg shadow flex flex-col gap-4"
            onSubmit={(e) => e.preventDefault()}
          >
            {!puedeEditar && (
              <div className="text-gray-700 bg-white p-2 rounded border">
                Estás en <strong>solo lectura</strong>.
              </div>
            )}
            {puedeEditar && !editando && (
              <div className="text-gray-700 bg-white p-2 rounded border text-center">
                Los campos están bloqueados. Pulsa <strong>Editar</strong>.
              </div>
            )}

            {/* Código */}
            <div>
              <label className="block font-semibold mb-1">Código</label>
              <input
                type="text"
                value={data.codigo || ""}
                disabled
                className="w-full border rounded p-2 bg-gray-100"
              />
            </div>

            {/* Fecha */}
            <div>
              <label className="block font-semibold mb-1">Fecha</label>
              <input
                type="date"
                value={data.fecha || ""}
                onChange={(e) => onChange("fecha", e.target.value)}
                className={`w-full border rounded p-2 ${
                  fieldDisabled ? "bg-gray-100" : "bg-white"
                }`}
                disabled={fieldDisabled}
                required
              />
            </div>

            {/* Sexo + Edad */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold mb-1">Sexo</label>
                <select
                  value={data.sexo || ""}
                  onChange={(e) => onChange("sexo", normalizeSexo(e.target.value))}
                  className={`w-full border rounded p-2 ${
                    fieldDisabled ? "bg-gray-100" : "bg-white"
                  }`}
                  disabled={fieldDisabled}
                  required
                >
                  <option value="">Seleccione una opción</option>
                  <option value="Femenino">Femenino</option>
                  <option value="Masculino">Masculino</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-1">Edad</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={data.edad ?? ""}
                  onChange={(e) => onChange("edad", e.target.value)}
                  className={`w-full border rounded p-2 ${
                    fieldDisabled ? "bg-gray-100" : "bg-white"
                  }`}
                  disabled={fieldDisabled}
                  min="0"
                />
              </div>
            </div>

            {/* Peso / Talla / IMC */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block font-semibold mb-1">Peso (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  value={data.peso ?? ""}
                  onChange={(e) => onChange("peso", e.target.value)}
                  className={`w-full border rounded p-2 ${
                    fieldDisabled ? "bg-gray-100" : "bg-white"
                  }`}
                  disabled={fieldDisabled}
                  min="0"
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">Talla (cm)</label>
                <input
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  value={data.talla ?? ""}
                  onChange={(e) => onChange("talla", e.target.value)}
                  className={`w-full border rounded p-2 ${
                    fieldDisabled ? "bg-gray-100" : "bg-white"
                  }`}
                  disabled={fieldDisabled}
                  min="0"
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">IMC (kg/m²)</label>
                <input
                  type="text"
                  value={imc != null ? imc.toFixed(2) : ""}
                  readOnly
                  className="w-full border rounded p-2 bg-gray-100"
                  placeholder="—"
                />
              </div>
            </div>

            {/* Botones edición */}
            <div className="flex flex-col sm:flex-row gap-2 justify-between items-stretch sm:items-center">
              {puedeEditar ? (
                <div className="flex gap-2">
                  {!editando ? (
                    <Button type="button" onClick={() => setEditando(true)}>
                      Editar
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="button"
                        onClick={() => {
                          setData(originalData);
                          setEditando(false);
                        }}
                        className="bg-gray-200"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        onClick={guardar}
                        className="bg-degluapp-green hover:bg-degluapp-green-hover text-white"
                        disabled={guardando}
                      >
                        {guardando ? "Guardando..." : "Guardar"}
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div />
              )}

              {puedeEditar && (
                <Button
                  type="button"
                  onClick={eliminarPaciente}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={eliminando}
                >
                  {eliminando ? "Eliminando..." : "Eliminar"}
                </Button>
              )}
            </div>
          </form>

          {/* VISITAS */}
          <div className="bg-white p-4 rounded-lg border flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h5 className="font-semibold">Visitas</h5>
              {puedeEditar && (
                <Button type="button" onClick={crearVisita}>
                  Nueva visita
                </Button>
              )}
            </div>

            {/* Aviso */}
            <div className="text-center text-gray-700 bg-[#f7fbff] border rounded p-2">
              <strong>Aviso:</strong> Los datos de arriba son la <strong>visita base</strong>. 
              Abajo se listan las visitas de seguimiento, <strong>numeradas</strong> por orden de creación.
            </div>

            {/* ⭐ BUSCADOR NUEVO */}
            <div className="mt-2">
              <input
                type="text"
                value={searchVisita}
                onChange={(e) => setSearchVisita(e.target.value)}
                placeholder="Buscar visita por número, fecha o estado…"
                className="w-full border rounded p-2"
              />
            </div>

            {/* Listado de visitas */}
            {visitas.length === 0 ? (
              <p className="text-gray-600">Aún no hay visitas.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {visitasFiltradas.map((v) => {
                  const numero = numeroVisitaPorId[v.id] || "—";
                  const estadoText =
                    v?.estado || (v?.completada ? "Completo" : "Pendiente");
                  const estadoIcon = estadoText === "Completo" ? "✅" : "⏳";

                  return (
                    <li
                      key={v.id}
                      className="p-3 rounded border bg-[#f7fbff] flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium">
                          {`Visita ${numero}`} — {formatDateDMY(v.fecha)}
                        </div>
                        <div className="text-gray-600">
                          {estadoIcon} {estadoText}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          onClick={() =>
                            navigate(`/pacientes/${docId}/visitas/${v.id}`)
                          }
                        >
                          Abrir
                        </Button>

                        {puedeEditar && (
                          <Button
                            type="button"
                            onClick={() => eliminarVisita(v.id)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            disabled={eliminandoVisitaId === v.id}
                          >
                            {eliminandoVisitaId === v.id
                              ? "Eliminando..."
                              : "Eliminar"}
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}